import { keyBy } from 'lodash';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMixpanel } from 'react-mixpanel-browser';

import { ANALYTICS_SET_REPORTS, ANALYTICS_SORT } from '../../../store/reducers/analytics';
import { cancellationsReportColumns } from './reportsColumnNames';
import { removeHiddenColumns } from './reportsColumnNames/utils';
import { AnalyticsReportType, exportReport } from '../../../store/actions/analytics/export-reports';
import { REPORT_TYPES, BY_DATE_TYPE_ORDER_CREATED } from './consts';
import { reportBusinessActionToMixpanel } from '../../../utils/reportBusinessActionToMixpanel';
import { setDateColumnByDateType } from '../../pages/Analytics/utils';
import { getInitialReportState } from '../../../utils/analytics';
import { useVisibleColumnsToggler } from '../useVisibleColumnsToggler';

import { CancellationTableRow } from './CancellationsTableRow';
import { ExportReportPopupToggler } from '../../pages/Reports';
import { ReportPagination } from './ReportPagination';
import { SendReportNotificationDialog } from './SendReportNotificationDialog';
import { EmptyChart } from '../../pages/Analytics/EmptyChart';
import { TableHeader } from '../TableHeader';
import { TableWrapper } from '../TableWrapper';
import { getCountryNameFromRow } from './utils';
import { AnalyticsSpinner } from '../../common/Spinner/AnalyticsSpinner';
import { EmptyPage } from '../../pages/Analytics/EmptyPage';

import styles from './ReportTable.module.scss';

export function CancellationsTable() {
  const dispatch = useDispatch();
  const mixpanel = useMixpanel();
  const { analytics, warehouses, supplier } = useSelector((state) => {
    return {
      analytics: state.analytics,
      warehouses: state.warehouses,
      supplier: state.role.supplier,
    };
  });
  const {
    reports,
    currentCurrency,
    countries,
    retailerNamesByIds,
    ui: {
      loading: { [REPORT_TYPES.CANCELLATIONS]: isLoading },
    },
    reports: {
      [REPORT_TYPES.CANCELLATIONS]: { canLoadData },
    },
  } = analytics;
  const { visibleColumns, dateType, sort, data: rawData } = reports[REPORT_TYPES.CANCELLATIONS];
  const { column: sortedBy, direction: sortDirection } = sort;
  const warehousesByIds = keyBy(warehouses.data, 'id');
  const availableColumns = removeHiddenColumns(cancellationsReportColumns);
  const { columnsDisplay, widget: visibleColumnsToggler } = useVisibleColumnsToggler({
    allColumns: availableColumns,
    visibleColumns,
    setVisibleColumns: (visibleColumns) =>
      dispatch({
        type: ANALYTICS_SET_REPORTS,
        payload: { reportType: REPORT_TYPES.CANCELLATIONS, data: { visibleColumns } },
      }),
  });

  const colSpan = visibleColumns.length;

  const uiData = React.useMemo(
    () =>
      rawData?.map((row) => ({
        ...row,
        retailerName: retailerNamesByIds?.[row.retailerId] ?? '',
        warehouseName: warehousesByIds[row.warehouseId]?.name,
        currency: currentCurrency,
        countryName: getCountryNameFromRow(countries, row.countryCode),
      })) ?? [],
    [rawData, currentCurrency, retailerNamesByIds, warehousesByIds, countries],
  );

  const onExport = ({ onlySelectedColumns }) => {
    reportBusinessActionToMixpanel(mixpanel, 'cancellations-report-export', { supplierName: supplier.name });
    dispatch(
      exportReport({
        reportType: AnalyticsReportType.ORDER_CANCELLATIONS,
        onlySelectedColumns,
        byDateType: BY_DATE_TYPE_ORDER_CREATED,
      }),
    );
  };

  return (
    <div className={styles.reportTableWrapper}>
      <SendReportNotificationDialog reportType="Cancellations" />
      <div className={styles.tableHeaderOptions}>
        {visibleColumnsToggler}
        <ExportReportPopupToggler title="Export Cancellations report" onExport={onExport} disabled={!uiData.length} />
      </div>

      <TableWrapper hasExpandedRow={false} className={styles.reportTable}>
        <TableHeader
          key={dateType}
          columns={columnsDisplay}
          overrides={(columns, columnsData) => setDateColumnByDateType(columns, columnsData.cancellationDate, dateType)}
          columnsData={availableColumns}
          sort={{
            sortedBy: sortedBy,
            direction: sortDirection,
            initialColumn: getInitialReportState([], REPORT_TYPES.CANCELLATIONS).sort.column,
            onSortBy: (column, direction) => {
              dispatch({
                type: ANALYTICS_SORT,
                payload: { reportType: REPORT_TYPES.CANCELLATIONS, column, direction },
              });
            },
          }}
        />

        <tbody>
          {isLoading || !canLoadData ? (
            <tr>
              <td colSpan={colSpan} style={{ height: 300 }}>
                <AnalyticsSpinner />
              </td>
            </tr>
          ) : uiData ? (
            uiData.length > 0 ? (
              uiData.map((record) => (
                <CancellationTableRow
                  key={`${record.cancellationDate}:${record.countryCode}:${record.cymbioVariantId}:${record.retailerId}`}
                  dateType={dateType}
                  visibleColumns={visibleColumns}
                  row={record}
                />
              ))
            ) : (
              <tr>
                <td colSpan={visibleColumns.length}>
                  <EmptyChart />
                </td>
              </tr>
            )
          ) : (
            <tr>
              <td colSpan={colSpan}>
                <EmptyPage />
              </td>
            </tr>
          )}
        </tbody>
      </TableWrapper>

      <ReportPagination reportType={REPORT_TYPES.CANCELLATIONS} />
    </div>
  );
}
